import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  VideoCall as VideoIcon,
  Assignment as AssignmentIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface InterviewStep {
  id: string;
  label: string;
  description?: string;
  completed: boolean;
  active: boolean;
  icon?: React.ReactNode;
  meetingData?: {
    id: string;
    interviewType: 'in_person' | 'video' | 'phone';
    date: string;
    time: string;
    timezone: string;
    location?: string;
    videoLink?: string;
    phoneNumber?: string;
    notes?: string;
    status?: 'scheduled' | 'completed' | 'cancelled';
    createdAt?: string;
    updatedAt?: string;
  };
}

interface InterviewProgressStepsProps {
  steps: InterviewStep[];
  currentStep?: number;
  onStepClick?: (stepId: string, stepIndex: number) => void;
  variant?: 'default' | 'compact' | 'detailed';
}

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-root': {
    top: 22,
  },
  '&.MuiStepConnector-line': {
    height: 3,
    border: 0,
    backgroundColor: alpha(theme.palette.grey[300], 0.6),
    borderRadius: 1,
  },
  '&.MuiStepConnector-active .MuiStepConnector-line': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
  '&.MuiStepConnector-completed .MuiStepConnector-line': {
    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  },
}));

const ColorlibStepIcon = styled('div')<{ ownerState: { completed?: boolean; active?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: alpha(theme.palette.grey[300], 0.8),
    zIndex: 1,
    color: theme.palette.grey[600],
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    transition: 'all 0.3s ease-in-out',
    ...(ownerState.active && {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
      transform: 'scale(1.1)',
    }),
    ...(ownerState.completed && {
      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
    }),
  }),
);

const CompactStepIcon = styled('div')<{ ownerState: { completed?: boolean; active?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: alpha(theme.palette.grey[300], 0.8),
    zIndex: 1,
    color: theme.palette.grey[600],
    width: 32,
    height: 32,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    transition: 'all 0.3s ease-in-out',
    ...(ownerState.active && {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      color: 'white',
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
      transform: 'scale(1.05)',
    }),
    ...(ownerState.completed && {
      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
      color: 'white',
      boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.4)}`,
    }),
  }),
);

const InterviewProgressSteps: React.FC<InterviewProgressStepsProps> = ({
  steps,
  currentStep = 0,
  onStepClick,
  variant = 'default',
}) => {
  const theme = useTheme();

  const getStepIcon = (step: InterviewStep, index: number) => {
    if (step.completed) {
      return <CheckIcon sx={{ fontSize: variant === 'compact' ? '1rem' : '1.2rem' }} />;
    }
    
    if (step.icon) {
      return step.icon;
    }

    // If step has meeting data, show meeting-specific icon
    if (step.meetingData) {
      const meetingType = step.meetingData.interviewType;
      const iconSize = variant === 'compact' ? '0.9rem' : '1.1rem';
      
      switch (meetingType) {
        case 'video':
          return <VideoIcon sx={{ fontSize: iconSize }} />;
        case 'phone':
          return <PhoneIcon sx={{ fontSize: iconSize }} />;
        case 'in_person':
          return <LocationIcon sx={{ fontSize: iconSize }} />;
        default:
          return <ScheduleIcon sx={{ fontSize: iconSize }} />;
      }
    }

    // Default icons based on step index
    const defaultIcons = [
      <ScheduleIcon sx={{ fontSize: variant === 'compact' ? '0.9rem' : '1.1rem' }} />,
      <VideoIcon sx={{ fontSize: variant === 'compact' ? '0.9rem' : '1.1rem' }} />,
      <AssignmentIcon sx={{ fontSize: variant === 'compact' ? '0.9rem' : '1.1rem' }} />,
      <WorkIcon sx={{ fontSize: variant === 'compact' ? '0.9rem' : '1.1rem' }} />,
      <PersonIcon sx={{ fontSize: variant === 'compact' ? '0.9rem' : '1.1rem' }} />,
    ];

    return defaultIcons[index % defaultIcons.length];
  };

  const StepIconComponent = variant === 'compact' ? CompactStepIcon : ColorlibStepIcon;

  const CustomStepIcon = (props: any) => {
    const { active, completed, className } = props;
    const stepIndex = props.stepIndex || 0;
    const step = steps[stepIndex];

    return (
      <StepIconComponent
        ownerState={{ completed, active }}
        className={className}
        onClick={() => onStepClick?.(step.id, stepIndex)}
        sx={{ cursor: onStepClick ? 'pointer' : 'default' }}
      >
        {getStepIcon(step, stepIndex)}
      </StepIconComponent>
    );
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ 
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        borderRadius: 2,
        p: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="600" color="primary.main">
            Interview Progress
          </Typography>
          <Chip 
            label={`${steps.filter(s => s.completed).length}/${steps.length} Complete`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
        
        <Stepper
          alternativeLabel
          activeStep={currentStep}
          connector={<ColorlibConnector />}
          sx={{ '& .MuiStepLabel-root': { px: 1 } }}
        >
          {steps.map((step, index) => (
            <Step key={step.id} completed={step.completed}>
              <StepLabel
                StepIconComponent={(props) => <CustomStepIcon {...props} stepIndex={index} />}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.7rem',
                    fontWeight: step.active ? 600 : 400,
                    color: step.active ? theme.palette.primary.main : theme.palette.text.secondary,
                  },
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  }

  if (variant === 'detailed') {
    return (
      <Box sx={{ 
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
        borderRadius: 3,
        p: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight="700" color="primary.main">
            Interview Process
          </Typography>
          <Chip 
            label={`${steps.filter(s => s.completed).length}/${steps.length} Steps Complete`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        
        <Stepper
          orientation="vertical"
          activeStep={currentStep}
          connector={<ColorlibConnector />}
        >
          {steps.map((step, index) => (
            <Step key={step.id} completed={step.completed}>
              <StepLabel
                StepIconComponent={(props) => <CustomStepIcon {...props} stepIndex={index} />}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.9rem',
                    fontWeight: step.active ? 600 : 400,
                    color: step.active ? theme.palette.primary.main : theme.palette.text.secondary,
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={step.active ? 600 : 400}>
                    {step.label}
                  </Typography>
                  {step.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  }

  // Default variant
  return (
    <Box sx={{ 
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      borderRadius: 2,
      p: 2.5,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="600" color="primary.main">
          Interview Progress
        </Typography>
        <Chip 
          label={`${steps.filter(s => s.completed).length}/${steps.length} Complete`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: '0.75rem', height: 24 }}
        />
      </Box>
      
      <Stepper
        alternativeLabel
        activeStep={currentStep}
        connector={<ColorlibConnector />}
        sx={{ '& .MuiStepLabel-root': { px: 1 } }}
      >
        {steps.map((step, index) => (
          <Step key={step.id} completed={step.completed}>
            <StepLabel
              StepIconComponent={(props) => <CustomStepIcon {...props} stepIndex={index} />}
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '0.8rem',
                  fontWeight: step.active ? 600 : 400,
                  color: step.active ? theme.palette.primary.main : theme.palette.text.secondary,
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default InterviewProgressSteps;
